import JaikLogo from "@/components/agency/JaikLogo";
import { Link } from "@/lib/router-compat";
import { ArrowLeft } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-agency-bg text-agency-text">
      <SEOHead
        title="Terms of Service | jaik"
        description="Terms of Service for jaik web design services. Payments, subscriptions, refunds, intellectual property, and dispute resolution."
        canonical="https://jaikmarketing.com/terms"
      />
      <div className="container max-w-3xl py-16 sm:py-24">
        <Link to="/" aria-label="jaik home" className="block w-fit font-heading text-2xl font-bold tracking-tight text-agency-text mb-8">
          <JaikLogo />
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-sans text-sm text-agency-text-muted hover:text-agency-text transition-colors mb-10"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
          Terms of Service
          <span className="text-agency-accent">.</span>
        </h1>
        <p className="font-sans text-sm text-agency-text-muted mb-10">
          Effective April 30, 2026 · Last updated April 30, 2026
        </p>

        <div className="prose-sm font-sans text-agency-text-muted leading-relaxed space-y-8">
          <p>
            These Terms of Service ("Terms") govern your use of jaikmarketing.com (the "Site") and
            the web design services provided by Jaik (Design by Jaik, a web design studio operated by
            John Kariuki, sole proprietor, in Renton, WA) ("we," "us," or "our"). By using the Site or engaging
            our services, you agree to these Terms.
          </p>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">1. Services</h2>
            <p>
              Jaik provides custom web design and development services for contractors and small businesses,
              along with optional ongoing services such as hosting, maintenance, and search engine optimization (SEO).
              Web design projects have a one-time build fee ($450–$1,499) with monthly plans starting at $300/month,
              including hosting, maintenance, and ongoing optimization. Monthly SEO retainers are also available.
              Pricing, package details, and scope of work are outlined in any written agreement or invoice.
              Project work follows a build-fee upfront / monthly plan structure unless otherwise agreed in writing.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">2. Booking &amp; Strategy Calls</h2>
            <p>
              You may book a free 20-minute strategy call through our native scheduling system at /book.
              By booking, you agree to receive booking confirmation and reminder messages by email and (if you
              opted in to SMS) text. Calls are conducted via Zoom; a meeting link is generated automatically and
              sent to your email. You may reschedule or cancel through the link in your confirmation email.
              Repeated no-shows may result in our declining future bookings. We reserve the right to refuse or
              cancel a booking at our discretion.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">3. Payments, Subscriptions &amp; Refunds</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Deposits are non-refundable once work has commenced.</li>
              <li>The remaining project balance is due upon project completion and before the site goes live.</li>
              <li>Monthly hosting and monthly SEO retainers are <strong className="text-agency-text">recurring subscriptions</strong> billed in advance via Stripe and automatically renew until cancelled. You may cancel at any time with no penalty; access continues through the end of the current billing period and no partial-month or pro-rated refunds are issued.</li>
              <li>You authorize us (via Stripe) to charge your saved payment method for each recurring billing cycle until you cancel.</li>
              <li>Failed or disputed payments may result in pausing or terminating ongoing services until resolved.</li>
              <li>Payments are processed securely via Stripe. We do not store your full credit card information; we retain only Stripe customer, subscription, and transaction identifiers necessary for billing and accounting.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">4. Onboarding</h2>
            <p>
              After payment, you will be invited to complete an onboarding questionnaire hosted on JotForm to
              collect the brand assets, content, and business information needed to build your site. We commit
              to delivering an initial custom site plan or first-draft mockup within 48 hours of receiving your
              completed onboarding responses, subject to weekends, holidays, and the timely receipt of required
              materials from you.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">5. SMS Program</h2>
            <p className="mb-3">
              <strong className="text-agency-text">Program description:</strong> Low-volume SMS alerts and marketing from jaikmarketing.com forms (&lt;30 messages/month). Consent is required via checkbox on our forms and/or SMS reply YES/SUBSCRIBE. Reply <strong className="text-agency-text">STOP</strong> to end. Reply <strong className="text-agency-text">HELP</strong> for info. Standard message and data rates apply. Questions: <a href="mailto:ayealvink@gmail.com" className="text-agency-accent hover:underline">ayealvink@gmail.com</a>.
            </p>
            <p className="mb-3">
              By submitting a form on this Site with the SMS consent checkbox checked, you consent to
              receive SMS notifications from Jaik (John Kariuki, jaikmarketing.com) regarding your inquiry, booking,
              project updates, or follow-ups. Consent is not a condition of purchase. Message frequency varies.
            </p>
            <p>
              You may also opt in to receive occasional marketing communications (tips, offers, promotions)
              via a separate checkbox. This is entirely optional. You can withdraw marketing consent at any time
              by replying STOP or contacting us. See our <Link to="/privacy" className="text-agency-accent hover:underline">Privacy Policy</Link> for
              full data handling details.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">6. Email Communications</h2>
            <p>
              By creating an account, booking a call, or submitting a form, you consent to receive transactional
              emails from jaikmarketing.com and notify.jaikmarketing.com, including but not limited to: email
              verification, password resets, booking confirmations and reminders, payment and subscription receipts,
              and project-related notifications. These emails are essential to the operation of our services and
              are not marketing communications. All emails are authenticated via SPF, DKIM, and DMARC. Transactional
              emails include an unsubscribe mechanism where applicable.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">7. Site Personalization, Analytics &amp; A/B Testing</h2>
            <p>
              We may show different layouts, copy, prices, or promotional elements (such as exit-intent popups
              and sticky CTAs) to different visitors as part of ongoing A/B tests and personalization.
              Variant assignments are recorded with your anonymous session and may be tied to subsequent actions
              (such as bookings or purchases) for performance analysis. Analytics, marketing, and advertising
              tags are region gated and can be switched off at any time on our{" "}
              <Link to="/choices" className="text-agency-accent hover:underline">Privacy Choices</Link> page. See our{" "}
              <Link to="/privacy" className="text-agency-accent hover:underline">Privacy Policy</Link> for details.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">8. Intellectual Property</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong className="text-agency-text">During the project:</strong> All design files, code, and creative assets remain the property of Jaik until full payment has been received.</li>
              <li><strong className="text-agency-text">After full payment:</strong> You receive a perpetual, non-exclusive license to use the final delivered website design and content for your business purposes.</li>
              <li><strong className="text-agency-text">Portfolio rights:</strong> We reserve the right to display completed work in our portfolio, case studies, and marketing materials unless you request otherwise in writing.</li>
              <li><strong className="text-agency-text">Third-party assets:</strong> Stock images, fonts, and third-party libraries used in your project are subject to their respective licenses.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">9. Portfolio &amp; Case Studies</h2>
            <p>
              Our case study sections showcase live client websites we designed and maintain. Some business details,
              photographs, and contact information shown may have been modified to protect client privacy. All designs
              and functionality shown are original work created by our team. Portfolio projects are presented to
              demonstrate our capabilities and do not constitute endorsements by or affiliations with any third
              parties unless explicitly stated.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">10. Client Responsibilities</h2>
            <p>
              You agree to provide necessary content (text, images, logos, branding) in a timely manner.
              Project timelines assume prompt feedback; delays in client communication, including delays in completing
              the onboarding questionnaire, may extend delivery dates. You are responsible for the accuracy and
              legality of all content you provide and for ensuring you have the rights to any third-party material
              you submit.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">11. Account Security</h2>
            <p>
              If you create an account on our Site, you are responsible for maintaining the confidentiality of your
              login credentials and for all activities that occur under your account. You agree to notify us
              immediately of any unauthorized use. We implement industry-standard authentication measures including
              verified email domains, secure password hashing, encrypted sessions, and Row-Level Security on our
              database.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">12. Acceptable Use</h2>
            <p>
              You agree not to: (a) submit false, misleading, or fraudulent information through any form, booking,
              or checkout; (b) attempt to bypass rate limits, honeypot fields, or other anti-abuse measures; (c)
              use automated tools to scrape, harvest, or overload the Site; (d) attempt to access non-public areas
              of the Site or backend systems; (e) use the Site for any unlawful purpose. We reserve the right to
              refuse service, void bookings, or cancel subscriptions for violations.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">13. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Jaik shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, including but not limited to loss of profits,
              revenue, data, or business opportunities, arising from or related to your use of the Site or
              our services. Our total aggregate liability for any claim shall not exceed the total amount
              you paid to us in the 12 months preceding the claim for the specific service giving rise to the claim.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">14. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Jaik, its owner, and affiliates from any claims,
              damages, losses, or expenses (including reasonable attorney's fees) arising from your use
              of the Site, your breach of these Terms, or content you provide for your project.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">15. Dispute Resolution</h2>
            <p>
              Any dispute arising from these Terms or our services shall first be addressed through
              good-faith negotiation. If the dispute cannot be resolved within 30 days, it shall be
              settled by binding arbitration in accordance with the rules of the American Arbitration
              Association, conducted in King County, Washington. Each party shall bear its own costs.
              Nothing in this section prevents either party from seeking injunctive relief in a court
              of competent jurisdiction.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">16. Termination</h2>
            <p>
              Either party may terminate a project or service agreement with written notice. Upon
              termination: (a) you are responsible for payment for all work completed to date;
              (b) deposits are non-refundable; (c) recurring subscriptions (hosting, SEO) may be
              cancelled at any time and will end at the close of the current billing period with no
              further obligation.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">17. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of
              Washington, United States, without regard to its conflict-of-law principles.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">18. Changes to These Terms</h2>
            <p>
              We reserve the right to update these Terms at any time. Changes will be posted on this page
              with an updated effective date. Material changes will be communicated via email or a prominent notice on our Site. Your continued use of the Site after changes constitutes
              acceptance of the updated Terms.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-agency-text mb-2">Contact</h2>
            <p>
              Design by Jaik<br />
              Email: <a href="mailto:ayealvink@gmail.com" className="text-agency-accent hover:underline">ayealvink@gmail.com</a><br />
              Phone: (253) 293-1550<br />
              Renton, WA, United States
            </p>
          </div>
        </div>

        <footer className="mt-16 pt-8 border-t border-agency-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-sans text-xs text-agency-text-muted/60">
            © Jaik {new Date().getFullYear()}
          </p>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-sans text-xs text-agency-text-muted/60 hover:text-agency-text-muted transition-colors">Home</Link>
            
            <Link to="/privacy" className="font-sans text-xs text-agency-text-muted/60 hover:text-agency-text-muted transition-colors">Privacy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TermsOfService;
